import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  CreditCard, CheckCircle, XCircle, Copy, RefreshCw, Save, Eye, EyeOff,
  Database, ServerCog, AlertTriangle, ExternalLink, Loader2, Zap, ShieldCheck, FlaskConical
} from 'lucide-react';
import api from '@/services/api';

const MODE_META = {
  live: {
    label: 'Live',
    icon: ShieldCheck,
    accent: 'emerald',
    pillBg: 'bg-emerald-100',
    pillText: 'text-emerald-700',
    pillBorder: 'border-emerald-300',
    cardBorder: 'border-emerald-300',
    cardRing: 'ring-emerald-200',
    activeBg: 'bg-emerald-50',
    softText: 'text-emerald-700',
    softBg: 'bg-emerald-50',
    softBorder: 'border-emerald-200',
    btn: 'bg-emerald-600 hover:bg-emerald-700 text-white',
    placeholder: 'sk_live_…',
    publishablePh: 'pk_live_…',
  },
  test: {
    label: 'Test',
    icon: FlaskConical,
    accent: 'amber',
    pillBg: 'bg-amber-100',
    pillText: 'text-amber-700',
    pillBorder: 'border-amber-300',
    cardBorder: 'border-amber-300',
    cardRing: 'ring-amber-200',
    activeBg: 'bg-amber-50',
    softText: 'text-amber-700',
    softBg: 'bg-amber-50',
    softBorder: 'border-amber-200',
    btn: 'bg-amber-600 hover:bg-amber-700 text-white',
    placeholder: 'sk_test_…',
    publishablePh: 'pk_test_…',
  },
};

const ModeKeyCard = ({ mode, data, activeMode, onActivate, onSave, onClear, activating, savingMode }) => {
  const meta = MODE_META[mode];
  const Icon = meta.icon;
  const isActive = activeMode === mode;
  const [secret, setSecret] = useState('');
  const [publishable, setPublishable] = useState('');
  const [showSecret, setShowSecret] = useState(false);

  const handleLocalSave = async () => {
    if (!secret.trim()) { toast.error(`${meta.label} secret key is required`); return; }
    const ok = mode === 'live' ? secret.startsWith('sk_live_') : (secret.startsWith('sk_test_') || secret.startsWith('rk_'));
    if (!ok) { toast.error(`Key must start with ${mode === 'live' ? 'sk_live_' : 'sk_test_'}`); return; }
    await onSave(mode, secret.trim(), publishable.trim());
    setSecret(''); setPublishable('');
  };

  return (
    <Card
      className={`relative bg-white transition-all ${
        isActive
          ? `border-2 ${meta.cardBorder} shadow-md ring-2 ${meta.cardRing}`
          : 'border border-slate-200'
      }`}
      data-testid={`stripe-mode-card-${mode}`}
    >
      {isActive && (
        <div className={`absolute -top-3 left-4 inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full ${meta.pillBg} ${meta.pillText} text-xs font-bold border ${meta.pillBorder} shadow-sm`}>
          <Zap className="w-3 h-3" /> CURRENTLY ACTIVE
        </div>
      )}
      <CardHeader className={`${isActive ? meta.activeBg : ''} rounded-t-lg`}>
        <div className="flex items-center justify-between">
          <CardTitle className="text-slate-900 flex items-center gap-2 text-base">
            <Icon className={`w-5 h-5 ${meta.softText}`} />
            {meta.label} Mode
            {mode === 'live' && <Badge className="bg-rose-50 text-rose-700 border border-rose-200 text-[10px]">Real charges</Badge>}
            {mode === 'test' && <Badge className="bg-slate-100 text-slate-600 border border-slate-200 text-[10px]">Sandbox</Badge>}
          </CardTitle>
          {data?.is_configured ? (
            <Badge className="bg-slate-100 text-slate-700 border border-slate-200 text-xs gap-1">
              <CheckCircle className="w-3 h-3 text-emerald-600" /> Saved
            </Badge>
          ) : (
            <Badge className="bg-slate-50 text-slate-500 border border-slate-200 text-xs">Not saved</Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4 pt-4">
        {data?.is_configured ? (
          <div className="space-y-2">
            <div className="p-2.5 bg-slate-50 border border-slate-200 rounded-lg">
              <p className="text-[11px] text-slate-500 mb-0.5">Secret Key</p>
              <code className="text-sm text-slate-900 font-mono" data-testid={`stripe-${mode}-secret-masked`}>{data.api_key_masked}</code>
            </div>
            <div className="p-2.5 bg-slate-50 border border-slate-200 rounded-lg">
              <p className="text-[11px] text-slate-500 mb-0.5">Publishable Key</p>
              <code className="text-sm text-slate-900 font-mono" data-testid={`stripe-${mode}-pub-masked`}>
                {data.publishable_key_masked || '— not set —'}
              </code>
            </div>
            {data.account && (
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div className="p-2 bg-slate-50 border border-slate-200 rounded">
                  <p className="text-slate-500">Account</p>
                  <code className="text-slate-900 font-mono">{data.account.id}</code>
                </div>
                <div className="p-2 bg-slate-50 border border-slate-200 rounded">
                  <p className="text-slate-500">Email</p>
                  <p className="text-slate-900 truncate">{data.account.email || '—'}</p>
                </div>
              </div>
            )}
            {data.connection_error && (
              <div className="p-2 bg-rose-50 border border-rose-200 rounded-lg text-xs text-rose-700 flex items-start gap-1.5">
                <XCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                <span>{data.connection_error}</span>
              </div>
            )}
          </div>
        ) : (
          <div className={`p-3 ${meta.softBg} border ${meta.softBorder} rounded-lg text-xs ${meta.softText}`}>
            No {meta.label.toLowerCase()} key saved yet. Add it below to enable {mode === 'live' ? 'real payments' : 'sandbox testing'}.
            <span className="block mt-1 text-slate-500">If <code>STRIPE_API_KEY</code> in .env matches this mode, you can still activate it.</span>
          </div>
        )}

        {/* Activate / Clear controls — always visible so admins can switch env-fallback modes too */}
        <div className="flex gap-2 pt-1">
          <Button
            onClick={() => onActivate(mode)}
            disabled={isActive || activating === mode}
            className={isActive ? 'bg-slate-200 text-slate-500 cursor-default hover:bg-slate-200' : meta.btn}
            size="sm"
            data-testid={`stripe-${mode}-activate-btn`}
          >
            {activating === mode ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Zap className="w-3.5 h-3.5 mr-1.5" />}
            {isActive ? 'Currently Active' : `Switch to ${meta.label}`}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onClear(mode)}
            disabled={!data?.is_configured}
            title={!data?.is_configured ? 'No saved key for this mode' : ''}
            data-testid={`stripe-${mode}-clear-btn`}
          >
            Clear
          </Button>
        </div>

        <div className="pt-2 border-t border-slate-100 space-y-2">
          <Label className="text-slate-700 text-xs">Add / Replace {meta.label} Secret Key</Label>
          <div className="flex gap-2">
            <Input
              type={showSecret ? 'text' : 'password'}
              placeholder={meta.placeholder}
              value={secret}
              onChange={(e) => setSecret(e.target.value)}
              className="font-mono text-sm"
              data-testid={`stripe-${mode}-secret-input`}
            />
            <Button variant="outline" size="icon" onClick={() => setShowSecret(s => !s)} data-testid={`stripe-${mode}-secret-toggle`}>
              {showSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </Button>
          </div>
          <Input
            placeholder={`Publishable key (${meta.publishablePh}, optional)`}
            value={publishable}
            onChange={(e) => setPublishable(e.target.value)}
            className="font-mono text-sm"
            data-testid={`stripe-${mode}-publishable-input`}
          />
          <Button
            onClick={handleLocalSave}
            disabled={savingMode === mode || !secret.trim()}
            className={meta.btn}
            size="sm"
            data-testid={`stripe-${mode}-save-btn`}
          >
            {savingMode === mode ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1.5" />}
            Save {meta.label} Key
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export const AdminStripeTab = () => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activating, setActivating] = useState(null);
  const [savingMode, setSavingMode] = useState(null);
  const [testing, setTesting] = useState(false);

  const webhookUrl = `${window.location.origin}/api/stripe/webhook`;

  const loadConfig = async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/stripe/config');
      setConfig(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load Stripe config');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadConfig(); }, []);

  const copy = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied`);
  };

  const handleSave = async (mode, apiKey, publishableKey) => {
    setSavingMode(mode);
    try {
      const res = await api.post('/admin/stripe/config', {
        api_key: apiKey,
        publishable_key: publishableKey,
      });
      toast.success(`${res.data.mode.toUpperCase()} key saved & validated`);
      await loadConfig();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save Stripe config');
    } finally {
      setSavingMode(null);
    }
  };

  const handleActivate = async (mode) => {
    setActivating(mode);
    try {
      const res = await api.post('/admin/stripe/activate', { mode });
      toast.success(`Switched to ${res.data.active_mode.toUpperCase()} mode`);
      await loadConfig();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to switch mode');
    } finally {
      setActivating(null);
    }
  };

  const handleClear = async (mode) => {
    if (!window.confirm(`Remove the saved ${mode.toUpperCase()} key? Payments in ${mode} mode will fall back to STRIPE_API_KEY from .env.`)) return;
    try {
      await api.delete(`/admin/stripe/config?mode=${mode}`);
      toast.success(`${mode.toUpperCase()} key removed`);
      await loadConfig();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to remove key');
    }
  };

  const handleTest = async () => {
    setTesting(true);
    try {
      const res = await api.post('/admin/stripe/test');
      toast.success(`Connected — ${res.data.active_mode.toUpperCase()} mode (${res.data.account?.email || res.data.account?.id})`);
      await loadConfig();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Connection test failed');
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <Card className="bg-white border-slate-200 shadow-sm" data-testid="admin-stripe-loading">
        <CardContent className="p-12 flex items-center justify-center text-slate-500">
          <Loader2 className="w-5 h-5 mr-2 animate-spin" /> Loading Stripe configuration…
        </CardContent>
      </Card>
    );
  }

  const activeMode = config?.active_mode || 'live';
  const activeMeta = MODE_META[activeMode];
  const ActiveIcon = activeMeta.icon;

  return (
    <div className="space-y-6">
      {/* Active Mode Banner */}
      <Card
        className={`border-2 ${activeMeta.cardBorder} ${activeMeta.activeBg} shadow-sm`}
        data-testid="stripe-active-mode-banner"
      >
        <CardContent className="p-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className={`w-12 h-12 rounded-full ${activeMeta.pillBg} flex items-center justify-center border-2 ${activeMeta.pillBorder}`}>
              <ActiveIcon className={`w-6 h-6 ${activeMeta.softText}`} />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <Badge
                  className={`${activeMeta.pillBg} ${activeMeta.pillText} border ${activeMeta.pillBorder} text-xs font-bold tracking-wide`}
                  data-testid="stripe-mode-badge"
                >
                  {activeMode.toUpperCase()} MODE ACTIVE
                </Badge>
                <span className="inline-flex items-center gap-1 text-xs text-slate-600">
                  {config?.source === 'database' ? <><Database className="w-3 h-3" /> from database</> : <><ServerCog className="w-3 h-3" /> from .env</>}
                </span>
              </div>
              <p className="text-sm text-slate-700 mt-1">
                {activeMode === 'live'
                  ? '🟢 Real charges are being processed. Switch to TEST mode for safe sandbox testing.'
                  : '🟡 Test mode. No real money is being moved. Switch to LIVE when you are ready.'}
              </p>
            </div>
          </div>
          <div className="flex gap-2 shrink-0">
            <Button
              variant="outline"
              size="sm"
              onClick={handleTest}
              disabled={testing || !config?.is_configured}
              data-testid="stripe-test-btn"
            >
              {testing ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 mr-1.5" />}
              Test Connection
            </Button>
            <Button variant="ghost" size="sm" asChild>
              <a href="https://dashboard.stripe.com/apikeys" target="_blank" rel="noreferrer">
                <ExternalLink className="w-3.5 h-3.5 mr-1.5" /> Stripe
              </a>
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Description */}
      <div className="flex items-start gap-3 p-3 bg-slate-50 border border-slate-200 rounded-lg">
        <CreditCard className="w-5 h-5 text-indigo-600 mt-0.5 flex-shrink-0" />
        <div className="text-sm text-slate-700">
          <p className="font-semibold mb-0.5">Manage both Stripe modes side by side</p>
          <p className="text-xs text-slate-600">
            Save your <strong className="text-emerald-700">Live</strong> and <strong className="text-amber-700">Test</strong> keys independently. Use the <em>Switch to…</em> button on either card to toggle which mode is currently processing payments. Keys are validated against Stripe before being saved.
          </p>
        </div>
      </div>

      {/* Side-by-side mode cards */}
      <div className="grid lg:grid-cols-2 gap-5">
        <ModeKeyCard
          mode="live"
          data={config?.modes?.live}
          activeMode={activeMode}
          onActivate={handleActivate}
          onSave={handleSave}
          onClear={handleClear}
          activating={activating}
          savingMode={savingMode}
        />
        <ModeKeyCard
          mode="test"
          data={config?.modes?.test}
          activeMode={activeMode}
          onActivate={handleActivate}
          onSave={handleSave}
          onClear={handleClear}
          activating={activating}
          savingMode={savingMode}
        />
      </div>

      {config?.mode === 'live' && config?.is_configured && (
        <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-xl flex items-start gap-3" data-testid="stripe-live-warning">
          <AlertTriangle className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-emerald-900">You&apos;re processing real card charges.</p>
            <p className="text-xs text-emerald-700 mt-1">Verify with a small test transaction. Switch to TEST mode anytime for safe development.</p>
          </div>
        </div>
      )}

      {/* Webhook Card */}
      <Card className="bg-white border-slate-200 shadow-sm" data-testid="admin-stripe-webhook">
        <CardHeader>
          <CardTitle className="text-slate-900">Webhook Endpoint</CardTitle>
          <CardDescription className="text-slate-600">
            Add this URL in your Stripe Dashboard under <strong>Developers → Webhooks</strong>. The same URL works for both Live and Test — Stripe will sign events with the matching secret.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center gap-2">
            <code className="flex-1 p-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-800 font-mono overflow-x-auto" data-testid="stripe-webhook-url">
              {webhookUrl}
            </code>
            <Button variant="outline" size="sm" onClick={() => copy(webhookUrl, 'Webhook URL')} data-testid="stripe-webhook-copy">
              <Copy className="w-4 h-4 mr-1" />Copy
            </Button>
          </div>
          <div className="p-3 bg-indigo-50 border border-indigo-200 rounded-lg">
            <p className="text-xs font-medium text-indigo-900 mb-1.5">Recommended events to subscribe:</p>
            <ul className="text-xs text-indigo-700 space-y-0.5 font-mono">
              <li>• checkout.session.completed</li>
              <li>• payment_intent.succeeded</li>
              <li>• customer.subscription.updated</li>
              <li>• customer.subscription.deleted</li>
              <li>• invoice.payment_failed</li>
            </ul>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminStripeTab;
